import useUser from '@/composables/useUser';
import { isJwtExpired } from '@/scripts/jwt';

/**
 * Logs the user out and returns them to the login (splash) screen when the
 * current session's JWT has gone stale. Returns `true` when it triggered a
 * logout so the caller can short-circuit any further page setup.
 *
 * `isLoggedIn` is derived from the `UserId` in the ft-ss-auth cookie, not from
 * the operator-test-api JWT stored alongside it — so an expired token would
 * otherwise leave a dead session in place. The guard only acts while the user
 * still appears logged in: after `clearAuth()` the UserId is gone, so a
 * subsequent call is a no-op. That is what prevents a redirect loop back onto
 * the splash screen.
 */
export function enforceFreshSession(): boolean {
  const { isLoggedIn, jwtToken, clearAuth } = useUser();

  if (!isLoggedIn.value) {
    return false;
  }

  if (!isJwtExpired(jwtToken.value)) {
    return false;
  }

  clearAuth();

  // Hard reload (rather than relying on the reactive `isLoggedIn` flip) so any
  // CRM SDK state on `window` is fully reset — mirrors PageMenu's logout.
  if (typeof window !== 'undefined') {
    window.location.href = '/';
  }

  return true;
}
